import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useMentionableEntities } from '@cobliteam/hooks/src/useSearchStore';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import ShineBorder from '@cobliteam/ui-business/components/border/ShineBorder';
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAIAssistant } from '../store';
import AIBetaFlag from './AIBetaFlag';
import AIButtonFloat from './AIButtonFloat';
import AIEntityMentionsInput, { type AIEntityMentionsInputHandle, MENTIONS_DISABLED } from './AIEntityMentionsInput';
import { ALL_MENTIONABLE_ENTITY_TYPES, formatSelectedEntitiesForPrompt } from './AIEntityMentionsInput.utils';
import AIIcon from './AIIcon';
import { PromptInputSubmit } from './elements/PromptInput';

const AI_ROUTE_PREFIX = '/ai';

const AIAssistantFloatingInput = () => {
  const { t } = useTranslation('ai');
  const { isAISidepanelOpen, setPendingInput, openAISidepanel } = useAIAssistant();
  const { pathname } = useLocation();
  const [input, setInput] = useState('');
  const [selectedEntities, setSelectedEntities] = useState<ReturnType<typeof useMentionableEntities>['entities']>([]);
  const [isDismissed, setIsDismissed] = useState(false);
  /** Same storage key as the rest of the dashboard (`e2e/playwright/tools/localStorage.ts` seeds `preferences`). */
  const [preferences] = useLocalStorage<Preferences>('preferences');
  const isHiddenAssistantFloatingInput = Boolean(preferences?.hideAIAssistantFloatingInput);
  const mentionInputRef = useRef<AIEntityMentionsInputHandle>(null);
  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);
  const {
    entities,
    isLoading: isLoadingMentionEntities,
    hasError: hasMentionError,
  } = useMentionableEntities({
    vehicleGroupIds,
    entityTypes: ALL_MENTIONABLE_ENTITY_TYPES,
  });

  const composePendingInput = useCallback(() => {
    const trimmedInput = input.trim();
    const selectedEntitiesBlock = formatSelectedEntitiesForPrompt(selectedEntities);
    const composed = [trimmedInput, selectedEntitiesBlock].filter(Boolean).join('\n\n');
    return composed.trim();
  }, [input, selectedEntities]);

  const handleBeforeToggle = useCallback(() => {
    const composed = composePendingInput();
    if (composed) {
      setPendingInput(composed);
      setInput('');
      setSelectedEntities([]);
    }
  }, [composePendingInput, setPendingInput]);

  const { aiChat } = useFeatureFlags();

  const isHiddenRoute = pathname.startsWith(AI_ROUTE_PREFIX) || pathname.startsWith('/monitoring') || pathname.startsWith('/tracking');

  if (!aiChat) return null;
  if (isHiddenAssistantFloatingInput) return null;
  if (isHiddenRoute) return null;
  if (isAISidepanelOpen) return null;
  if (isDismissed) return <AIButtonFloat />;

  const hasContent = input.trim() || selectedEntities.length > 0;

  return (
    <div data-testid='ai-floating-input' className={cn('pointer-events-none fixed inset-x-0 bottom-16 z-[60] flex justify-center px-4')}>
      <div className='pointer-events-auto relative w-full max-w-[480px] rounded-lg shadow-md'>
        <ShineBorder borderWidth={2} className='absolute inset-0 rounded-lg' shineColor={['#0745cf', '#9847ee', '#a07cfe']} />
        <div className={cn('relative overflow-hidden rounded-lg p-[1.5px]')}>
          <div className='relative flex flex-col rounded-md bg-white shadow-md'>
            {/* Header */}
            <div className='flex items-center justify-between px-3 pt-2 pb-0'>
              <span className='font-semibold text-gray-11 text-sm'>
                Pergunte a <span className='bg-gradient-to-r from-[#0745cf] to-[#9847ee] bg-clip-text text-transparent'>Cobli IA</span>
                <AIBetaFlag className='ml-1.5' />
              </span>
              <Button
                variant='ghost'
                size='sm'
                icon={<Icon name='x' size='sm' />}
                className='h-8 w-8 p-0 text-gray-9 hover:text-gray-11'
                onClick={() => {
                  Analytics.track('AI - Chat - Dismissed', {
                    source: 'floating',
                  });
                  setIsDismissed(true);
                }}
                aria-label={t('assistant_float.aria_label')}
                data-testid='ai-floating-input-dismiss'
              />
            </div>

            {/* Divider */}
            <div className='px-0 py-0.5'>
              <div className='h-px w-full bg-gray-5' />
            </div>

            {/* Input row */}
            <form
              className='flex items-center px-3 pt-1 pb-2'
              onSubmit={(e) => {
                e.preventDefault();
                Analytics.track('AI - Chat - Message Sent', {
                  source: 'floating',
                });
                Analytics.track('AI - Chat - Open New Conversation', {
                  source: 'floating',
                });
                handleBeforeToggle();
                openAISidepanel();
              }}
            >
              <AIIcon size={32} isHovered sparklesPaint='brand-gradient' />
              <div className='min-w-0 flex-1'>
                <AIEntityMentionsInput
                  ref={mentionInputRef}
                  autoFocus={false}
                  placeholder={t('prompt_placeholder')}
                  value={input}
                  entities={entities}
                  selectedEntities={selectedEntities}
                  onSelectedEntitiesChange={setSelectedEntities}
                  onValueChange={setInput}
                  isLoadingEntities={isLoadingMentionEntities}
                  hasMentionError={hasMentionError}
                  minHeight={32}
                  maxHeight={80}
                  className='!text-sm'
                />
              </div>
              <div className='flex shrink-0 items-center gap-1'>
                {!MENTIONS_DISABLED && (
                  <Tooltip content={hasMentionError ? 'Menções indisponíveis no momento' : 'Adicionar menção'} asChild>
                    <div>
                      <Button
                        size='sm'
                        color='default'
                        className='size-8 rounded-[4px]'
                        variant='outlined'
                        disabled={hasMentionError}
                        onClick={() => {
                          Analytics.track('AI - Chat - Mention Triggered', {
                            source: 'floating',
                          });
                          mentionInputRef.current?.insertMentionTrigger();
                        }}
                      >
                        <span className='font-semibold text-xs leading-none'>@</span>
                      </Button>
                    </div>
                  </Tooltip>
                )}
                <div className='flex items-center gap-1.5'>
                  <PromptInputSubmit disabled={!hasContent} status='ready' variant='solid' />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantFloatingInput;
